import React, { useCallback, useMemo, useState } from 'react';
import { FlatList, Platform, Pressable, RefreshControl, ScrollView, Text, View } from 'react-native';
import { ActivityIndicator, Button, Snackbar } from 'react-native-paper';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useFocusEffect } from '@react-navigation/native';
import { CONTENT_MAX_WIDTH, RADIUS, TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import { Avatar, Badge, BadgeTone, FilterChip, PageHeader, SearchPill, toneColors } from '../components/ui';
import StatCard from '../components/StatCard';
import EmptyState from '../components/EmptyState';
import { auditService, AuditFilters } from '../services/auditService';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { usePagedList } from '../hooks/usePagedList';
import { AuditAction, AuditLogEntry, AuditSummary } from '../types';
import { roleLabel } from '../navigation/navItems';

type IconName = React.ComponentProps<typeof MaterialCommunityIcons>['name'];

const ACTION_META: Record<AuditAction, { label: string; icon: IconName; tone: BadgeTone }> = {
  CREATION: { label: 'Création', icon: 'plus', tone: 'success' },
  MODIFICATION: { label: 'Modification', icon: 'pencil-outline', tone: 'info' },
  SUPPRESSION: { label: 'Suppression', icon: 'trash-can-outline', tone: 'danger' },
  CONNEXION: { label: 'Connexion', icon: 'login', tone: 'brand' },
  ECHEC_CONNEXION: { label: 'Échec de connexion', icon: 'shield-alert-outline', tone: 'danger' },
  DECONNEXION: { label: 'Déconnexion', icon: 'logout', tone: 'neutral' },
  EXPORT: { label: 'Export', icon: 'download-outline', tone: 'warning' },
};

type ActionFilter = 'TOUT' | 'CREATION' | 'MODIFICATION' | 'SUPPRESSION' | 'SESSION' | 'EXPORT';

const ACTION_FILTERS: { key: ActionFilter; label: string; icon: IconName; actions?: AuditAction[] }[] = [
  { key: 'TOUT', label: 'Tout', icon: 'format-list-bulleted' },
  { key: 'CREATION', label: 'Créations', icon: 'plus', actions: ['CREATION'] },
  { key: 'MODIFICATION', label: 'Modifications', icon: 'pencil-outline', actions: ['MODIFICATION'] },
  { key: 'SUPPRESSION', label: 'Suppressions', icon: 'trash-can-outline', actions: ['SUPPRESSION'] },
  { key: 'SESSION', label: 'Connexions', icon: 'login', actions: ['CONNEXION', 'ECHEC_CONNEXION', 'DECONNEXION'] },
  { key: 'EXPORT', label: 'Exports', icon: 'download-outline', actions: ['EXPORT'] },
];

type Period = 'TOUT' | 'JOUR' | 'SEMAINE' | 'MOIS';

const PERIODS: { key: Period; label: string }[] = [
  { key: 'JOUR', label: "Aujourd'hui" },
  { key: 'SEMAINE', label: '7 jours' },
  { key: 'MOIS', label: '30 jours' },
  { key: 'TOUT', label: 'Tout' },
];

function periodStart(period: Period): string | undefined {
  if (period === 'TOUT') return undefined;
  const date = new Date();
  if (period === 'JOUR') {
    date.setHours(0, 0, 0, 0);
  } else {
    date.setDate(date.getDate() - (period === 'SEMAINE' ? 7 : 30));
  }
  return date.toISOString();
}

const dayKey = (iso: string) => new Date(iso).toDateString();

function dayLabel(iso: string) {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
  if (date.toDateString() === yesterday.toDateString()) return 'Hier';
  const label = date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

const timeLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

function deviceOf(userAgent?: string | null): { icon: IconName; label: string } {
  const ua = userAgent ?? '';
  if (/okhttp|expo/i.test(ua)) return { icon: 'cellphone', label: 'Application mobile' };
  if (/mobile|android|iphone|ipad/i.test(ua)) return { icon: 'cellphone', label: 'Navigateur mobile' };
  if (!ua) return { icon: 'help-circle-outline', label: 'Appareil inconnu' };
  if (!/mozilla/i.test(ua)) return { icon: 'api', label: 'Client API / script' };
  return { icon: 'monitor', label: 'Navigateur' };
}

function prettyJson(details?: string | null) {
  if (!details) return null;
  try {
    return JSON.stringify(JSON.parse(details), null, 2);
  } catch {
    return details;
  }
}

export default function HistoriqueActions() {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { isDesktop, gutter } = useLayout();

  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState<ActionFilter>('TOUT');
  const [period, setPeriod] = useState<Period>('SEMAINE');
  const [username, setUsername] = useState<string | undefined>(undefined);
  const [failuresOnly, setFailuresOnly] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [summary, setSummary] = useState<AuditSummary | null>(null);
  const [usernames, setUsernames] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(search, 300);
  const searchQuery = search.trim() ? debouncedSearch.trim() : '';
  const from = useMemo(() => periodStart(period), [period]);

  const filters = useMemo<AuditFilters>(() => ({
    q: searchQuery || undefined,
    username,
    actions: ACTION_FILTERS.find((f) => f.key === actionFilter)?.actions,
    succes: failuresOnly ? false : undefined,
    from,
  }), [searchQuery, username, actionFilter, failuresOnly, from]);

  const queryKey = JSON.stringify(filters);
  const fetchPage = useCallback(
    (page: number, size: number) => auditService.getPage(page, size, filters),
    [filters],
  );
  const { items, total, loading, loadingMore, hasMore, refresh, loadMore, error } = usePagedList(fetchPage, queryKey, 40);

  const loadOverview = useCallback(() => {
    auditService.getSummary().then(setSummary).catch(() => setSummary(null));
    auditService.getUsernames().then(setUsernames).catch(() => setUsernames([]));
  }, []);

  useFocusEffect(loadOverview);

  const onRefresh = () => {
    refresh();
    loadOverview();
  };

  const onExport = async () => {
    setExporting(true);
    try {
      await auditService.exportCsv(filters);
      setMessage('Export CSV généré.');
    } catch (e: any) {
      setMessage(e?.message ?? "Impossible d'exporter l'historique.");
    } finally {
      setExporting(false);
    }
  };

  const column = { paddingHorizontal: gutter, maxWidth: CONTENT_MAX_WIDTH, width: '100%' as const, alignSelf: 'center' as const };

  const header = (
    <View style={[column, { paddingTop: isDesktop ? 32 : 16 }]}>
      <PageHeader
        eyebrow="Administration"
        title="Historique des actions"
        subtitle="Archive de toutes les actions effectuées dans l'application, et par qui."
        actions={
          <Button
            mode="outlined"
            icon="tray-arrow-down"
            textColor={colors.textPrimary}
            onPress={onExport}
            loading={exporting}
            disabled={exporting}
          >
            Exporter (CSV)
          </Button>
        }
      />

      <View style={styles.kpis}>
        <StatCard label="Actions (24 h)" value={String(summary?.actions24h ?? '—')} icon="lightning-bolt-outline" tone="primary" style={styles.kpi} />
        <StatCard label="Actions (7 jours)" value={String(summary?.actions7Jours ?? '—')} icon="calendar-week" tone="brand" style={styles.kpi} />
        <StatCard label="Échecs (7 jours)" value={String(summary?.echecs7Jours ?? '—')} icon="alert-circle-outline" tone="danger" style={styles.kpi} />
        <StatCard label="Utilisateurs actifs" value={String(summary?.utilisateursActifs7Jours ?? '—')} hint="sur 7 jours" icon="account-multiple-outline" tone="success" style={styles.kpi} />
      </View>

      <SearchPill
        value={search}
        onChangeText={setSearch}
        placeholder="Rechercher une action, un client, une adresse IP…"
        loading={loading && !!searchQuery}
        style={styles.search}
      />

      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chipRow}>
        {ACTION_FILTERS.map((filter) => (
          <FilterChip
            key={filter.key}
            label={filter.label}
            icon={filter.icon}
            selected={actionFilter === filter.key}
            onPress={() => setActionFilter(filter.key)}
          />
        ))}
        <View style={styles.chipSeparator} />
        <FilterChip
          label="Échecs uniquement"
          icon="alert-circle-outline"
          selected={failuresOnly}
          onPress={() => setFailuresOnly((value) => !value)}
        />
      </ScrollView>

      <View style={[styles.filterLine, !isDesktop && styles.filterLineMobile]}>
        <View style={styles.filterGroup}>
          <Text style={styles.filterLabel}>Période</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chipRowCompact}>
            {PERIODS.map((p) => (
              <FilterChip key={p.key} label={p.label} selected={period === p.key} onPress={() => setPeriod(p.key)} />
            ))}
          </ScrollView>
        </View>
        {usernames.length > 0 ? (
          <View style={[styles.filterGroup, styles.filterGroupGrow]}>
            <Text style={styles.filterLabel}>Utilisateur</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chipRowCompact}>
              <FilterChip label="Tous" selected={!username} onPress={() => setUsername(undefined)} />
              {usernames.map((name) => (
                <FilterChip
                  key={name}
                  label={name}
                  icon="account-outline"
                  selected={username === name}
                  onPress={() => setUsername(username === name ? undefined : name)}
                />
              ))}
            </ScrollView>
          </View>
        ) : null}
      </View>

      <Text style={styles.resultCount}>
        {loading && items.length === 0 ? 'Chargement…' : `${total} action${total > 1 ? 's' : ''} archivée${total > 1 ? 's' : ''}`}
      </Text>
    </View>
  );

  const renderItem = ({ item, index }: { item: AuditLogEntry; index: number }) => {
    const previous = index > 0 ? items[index - 1] : undefined;
    const showDay = !previous || dayKey(previous.dateAction) !== dayKey(item.dateAction);
    return (
      <View style={column}>
        {showDay ? <Text style={styles.dayHeader}>{dayLabel(item.dateAction)}</Text> : null}
        <AuditRow
          entry={item}
          expanded={expandedId === item.id}
          onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
          isDesktop={isDesktop}
        />
      </View>
    );
  };

  return (
    <View style={styles.root}>
      <FlatList
        data={items}
        keyExtractor={(item) => String(item.id)}
        renderItem={renderItem}
        ListHeaderComponent={header}
        ListEmptyComponent={
          loading ? (
            <View style={styles.center}><ActivityIndicator color={colors.primary} /></View>
          ) : (
            <EmptyState
              icon="history"
              message={error ?? 'Aucune action trouvée'}
              hint={error ? undefined : 'Modifiez les filtres ou la période pour élargir la recherche.'}
            />
          )
        }
        ListFooterComponent={
          loadingMore ? (
            <View style={styles.footer}><ActivityIndicator color={colors.primary} /></View>
          ) : !hasMore && items.length > 0 ? (
            <Text style={styles.endText}>Fin de l'historique pour ces filtres</Text>
          ) : null
        }
        onEndReached={() => { if (hasMore) loadMore(); }}
        onEndReachedThreshold={0.4}
        refreshControl={<RefreshControl refreshing={false} onRefresh={onRefresh} colors={[colors.primary]} tintColor={colors.primary} />}
        contentContainerStyle={styles.listContent}
        keyboardShouldPersistTaps="handled"
      />
      <Snackbar visible={!!message} onDismiss={() => setMessage(null)} duration={3500}>
        {message ?? ''}
      </Snackbar>
    </View>
  );
}

type AuditRowProps = {
  entry: AuditLogEntry;
  expanded: boolean;
  onToggle: () => void;
  isDesktop: boolean;
};

function AuditRow({ entry, expanded, onToggle, isDesktop }: AuditRowProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const meta = ACTION_META[entry.action] ?? ACTION_META.MODIFICATION;
  const tone = toneColors(colors, entry.succes ? meta.tone : 'danger');
  const device = deviceOf(entry.userAgent);
  const details = expanded ? prettyJson(entry.details) : null;

  return (
    <Pressable
      onPress={onToggle}
      accessibilityRole="button"
      accessibilityState={{ expanded }}
      accessibilityLabel={`${entry.description ?? meta.label}, par ${entry.username ?? 'inconnu'} à ${timeLabel(entry.dateAction)}`}
      style={(state) => {
        const hovered = (state as { hovered?: boolean }).hovered;
        return [styles.row, (hovered || expanded) && styles.rowHover];
      }}
    >
      <View style={styles.rowMain}>
        {isDesktop ? <Text style={styles.time}>{timeLabel(entry.dateAction)}</Text> : null}
        <View style={[styles.actionIcon, { backgroundColor: tone.bg }]}>
          <MaterialCommunityIcons name={entry.succes ? meta.icon : 'alert-circle-outline'} size={20} color={tone.fg} />
        </View>
        <View style={styles.rowText}>
          <Text style={styles.description} numberOfLines={expanded ? undefined : 2}>
            {entry.description ?? meta.label}
          </Text>
          <View style={styles.metaLine}>
            <Avatar name={entry.username ?? '?'} size={20} variant="soft" />
            <Text style={styles.metaStrong} numberOfLines={1}>{entry.username ?? 'Inconnu'}</Text>
            <Text style={styles.meta} numberOfLines={1}>
              {[entry.role ? roleLabel(entry.role) : null, !isDesktop ? timeLabel(entry.dateAction) : null, entry.adresseIp]
                .filter(Boolean)
                .join(' · ')}
            </Text>
          </View>
        </View>
        <View style={[styles.badges, !isDesktop && styles.badgesMobile]}>
          <Badge label={meta.label} tone={meta.tone} />
          {!entry.succes ? <Badge label={`Échec${entry.statutHttp ? ` ${entry.statutHttp}` : ''}`} tone="danger" icon="close" /> : null}
          <MaterialCommunityIcons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={22}
            color={colors.textMuted}
            style={styles.chevron}
          />
        </View>
      </View>

      {expanded ? (
        <View style={[styles.detailBox, isDesktop && styles.detailBoxDesktop]}>
          <DetailLine label="Date" value={new Date(entry.dateAction).toLocaleString('fr-FR')} />
          <DetailLine label="Élément" value={[entry.entite, entry.entiteId ? `#${entry.entiteId}` : null].filter(Boolean).join(' ') || '—'} />
          <DetailLine label="Requête" value={[entry.methode, entry.chemin].filter(Boolean).join(' ') || '—'} mono />
          <DetailLine label="Résultat" value={`${entry.succes ? 'Succès' : 'Échec'}${entry.statutHttp ? ` (HTTP ${entry.statutHttp})` : ''}`} />
          <DetailLine label="Appareil" value={device.label} icon={device.icon} />
          {entry.adresseIp ? <DetailLine label="Adresse IP" value={entry.adresseIp} mono /> : null}
          {details ? (
            <View style={styles.jsonWrap}>
              <Text style={styles.detailLabel}>Données envoyées</Text>
              <ScrollView horizontal style={styles.jsonScroll}>
                <Text style={styles.json} selectable>{details}</Text>
              </ScrollView>
            </View>
          ) : null}
        </View>
      ) : null}
    </Pressable>
  );
}

function DetailLine({ label, value, mono, icon }: { label: string; value: string; mono?: boolean; icon?: IconName }) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  return (
    <View style={styles.detailLine}>
      <Text style={styles.detailLabel}>{label}</Text>
      <View style={styles.detailValueRow}>
        {icon ? <MaterialCommunityIcons name={icon} size={16} color={colors.textSecondary} /> : null}
        <Text style={[styles.detailValue, mono && styles.mono]} selectable>{value}</Text>
      </View>
    </View>
  );
}

const MONO_FONT = Platform.select({ ios: 'Menlo', android: 'monospace', default: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' });

const useStyles = makeStyles((c) => ({
  root: { flex: 1, backgroundColor: c.background },
  listContent: { paddingBottom: 48 },
  center: { paddingVertical: 48, alignItems: 'center' },
  footer: { paddingVertical: 24, alignItems: 'center' },
  endText: { textAlign: 'center', color: c.textMuted, fontSize: 13, paddingVertical: 24 },

  kpis: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 24 },
  kpi: { minWidth: 150 },
  search: { marginBottom: 16 },
  chipRow: { gap: 8, paddingBottom: 4, alignItems: 'center' },
  chipRowCompact: { gap: 8, alignItems: 'center' },
  chipSeparator: { width: 1, height: 24, backgroundColor: c.divider, marginHorizontal: 4 },
  filterLine: { flexDirection: 'row', gap: 24, marginTop: 16, alignItems: 'flex-start' },
  filterLineMobile: { flexDirection: 'column', gap: 12 },
  filterGroup: { gap: 8, flexShrink: 0, maxWidth: '100%' },
  filterGroupGrow: { flexShrink: 1, minWidth: 0 },
  filterLabel: { ...TYPOGRAPHY.overline, color: c.textMuted, textTransform: 'uppercase' },
  resultCount: { ...TYPOGRAPHY.label, color: c.textSecondary, marginTop: 24, marginBottom: 4 },

  dayHeader: {
    ...TYPOGRAPHY.subheading,
    color: c.textPrimary,
    paddingTop: 24,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: c.divider,
  },
  row: {
    paddingVertical: 14,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: c.divider,
    borderRadius: 0,
  },
  rowHover: { backgroundColor: c.surfaceAlt, borderRadius: RADIUS.md },
  rowMain: { flexDirection: 'row', alignItems: 'center', gap: 14, flexWrap: 'wrap' },
  time: { width: 48, fontSize: 13, fontWeight: '600', color: c.textSecondary, fontVariant: ['tabular-nums'] },
  actionIcon: { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center' },
  rowText: { flex: 1, minWidth: 180 },
  description: { fontSize: 15, lineHeight: 21, fontWeight: '600', color: c.textPrimary },
  metaLine: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4 },
  metaStrong: { fontSize: 13, fontWeight: '600', color: c.textPrimary, flexShrink: 0 },
  meta: { fontSize: 13, color: c.textSecondary, flexShrink: 1 },
  badges: { flexDirection: 'row', gap: 6, alignItems: 'center', marginLeft: 'auto' },
  badgesMobile: { width: '100%', paddingLeft: 54 },
  chevron: { marginLeft: 'auto' },

  detailBox: {
    marginTop: 12,
    marginLeft: 54,
    padding: 16,
    borderRadius: RADIUS.md,
    backgroundColor: c.surface,
    borderWidth: 1,
    borderColor: c.divider,
    gap: 10,
  },
  detailBoxDesktop: { marginLeft: 116 },
  detailLine: { flexDirection: 'row', gap: 12, flexWrap: 'wrap' },
  detailLabel: { width: 110, fontSize: 13, color: c.textSecondary },
  detailValueRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1, minWidth: 160 },
  detailValue: { fontSize: 14, color: c.textPrimary, fontWeight: '500', flexShrink: 1 },
  mono: { fontFamily: MONO_FONT, fontSize: 13 },
  jsonWrap: { gap: 6, marginTop: 4 },
  jsonScroll: { borderRadius: RADIUS.sm, backgroundColor: c.surfaceAlt },
  json: { fontFamily: MONO_FONT, fontSize: 12, lineHeight: 18, color: c.textPrimary, padding: 12 },
}));
